/**
 * Settings → Company — workspace-level configuration.
 *
 * Single-form page for Conduct HQ company info, signature block,
 * and branding. SPEC §7.5: report footer signature comes from here.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

import { useWorkspace, useUpdateWorkspace } from '@/hooks/use-workspace.js';
import { Button } from '@/components/ui/button.js';

export function SettingsCompany() {
  const query = useWorkspace();
  const update = useUpdateWorkspace();
  const ws = query.data;

  const [companyName, setCompanyName] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureTitle, setSignatureTitle] = useState('');
  const [signatureEmail, setSignatureEmail] = useState('');
  const [signaturePhone, setSignaturePhone] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (ws) {
      setCompanyName(ws.companyName);
      setSignatureName(ws.signatureName);
      setSignatureTitle(ws.signatureTitle);
      setSignatureEmail(ws.signatureEmail);
      setSignaturePhone(ws.signaturePhone);
      setLogoUrl(ws.logoUrl ?? '');
    }
  }, [ws]);

  function markDirty<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setDirty(true);
    };
  }

  async function handleSave() {
    update.mutate(
      {
        companyName: companyName.trim(),
        signatureName: signatureName.trim(),
        signatureTitle: signatureTitle.trim(),
        signatureEmail: signatureEmail.trim(),
        signaturePhone: signaturePhone.trim(),
        logoUrl: logoUrl.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Company settings saved');
          setDirty(false);
        },
        onError: () => toast.error('Failed to save settings'),
      }
    );
  }

  if (query.isLoading) {
    return (
      <div className="p-6 space-y-4 max-w-2xl animate-pulse">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 bg-ink-700 rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h2 className="text-[15px] font-semibold text-ink-100">
          Company details
        </h2>
        <p className="text-[13px] text-ink-300 mt-0.5">
          Used in report headers, email footers, and client-facing pages.
        </p>
      </div>

      <div className="space-y-4">
        <Field label="Company name">
          <Input
            value={companyName}
            onChange={(e) => markDirty(setCompanyName)(e.target.value)}
            data-testid="ws-company-name"
          />
        </Field>

        <Field label="Logo URL">
          <Input
            type="url"
            value={logoUrl}
            onChange={(e) => markDirty(setLogoUrl)(e.target.value)}
            placeholder="https://..."
          />
        </Field>
      </div>

      <div>
        <h2 className="text-[15px] font-semibold text-ink-100">
          Signature block
        </h2>
        <p className="text-[13px] text-ink-300 mt-0.5">
          Appears at the bottom of client reports and outbound emails.
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name">
            <Input
              value={signatureName}
              onChange={(e) => markDirty(setSignatureName)(e.target.value)}
              data-testid="ws-sig-name"
            />
          </Field>
          <Field label="Title">
            <Input
              value={signatureTitle}
              onChange={(e) => markDirty(setSignatureTitle)(e.target.value)}
              data-testid="ws-sig-title"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email">
            <Input
              type="email"
              value={signatureEmail}
              onChange={(e) => markDirty(setSignatureEmail)(e.target.value)}
              data-testid="ws-sig-email"
            />
          </Field>
          <Field label="Phone">
            <Input
              type="tel"
              value={signaturePhone}
              onChange={(e) => markDirty(setSignaturePhone)(e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* Signature preview */}
      <div className="rounded-md border border-ink-600 p-4 bg-ink-800">
        <p className="text-[11px] uppercase tracking-[0.06em] text-ink-400 mb-2">
          Preview
        </p>
        <div className="text-[13px] text-ink-200 space-y-0.5">
          <p className="font-medium text-ink-100">{signatureName || '—'}</p>
          <p>{signatureTitle || '—'}</p>
          <p className="mono text-[12px]">{signatureEmail || '—'}</p>
          {signaturePhone && (
            <p className="mono text-[12px]">{signaturePhone}</p>
          )}
          <p className="text-ink-300 text-[12px] mt-1">
            {companyName || '—'}
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!dirty || update.isPending}
          data-testid="ws-save"
        >
          <Save size={13} />
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={
        'w-full h-9 px-3 rounded-md text-[13px] bg-ink-800 border border-ink-600 text-ink-100 placeholder:text-ink-500 focus-ring ' +
        (props.className ?? '')
      }
    />
  );
}
